/**
 * Test utilities for Image Library functionality
 * 
 * Usage in browser console:
 * - window.testImageLibrary.testModalOpen()
 * - window.testImageLibrary.testIcons()
 * - window.testImageLibrary.testStorageService()
 */

import { imageLibraryService } from '../lib/imageLibraryService.js'
import { storageService } from '../lib/storageService.js'
import { supabase } from '../lib/supabase.js'

class ImageLibraryTester {
  constructor() {
    console.log('🧪 Image Library Tester initialized')
  }

  /**
   * Test if the modal can open without errors
   */
  async testModalOpen() {
    console.log('🎭 Testing Image Library Modal...')
    
    try {
      // Test if React components can be imported
      const { default: ImageLibraryModal } = await import('../components/ImageLibrary/ImageLibraryModal.jsx')
      console.log('✅ ImageLibraryModal component imports successfully')
      
      // Test if all required icons are available
      const FiIcons = await import('react-icons/fi')
      const requiredIcons = ['FiX', 'FiSearch', 'FiCheck', 'FiGrid', 'FiList', 'FiZap', 'FiUpload']
      
      for (const iconName of requiredIcons) {
        if (FiIcons[iconName]) {
          console.log(`✅ ${iconName} icon available`)
        } else {
          console.error(`❌ ${iconName} icon missing!`)
        }
      }
      
      return true
    } catch (error) {
      console.error('❌ Modal test failed:', error)
      return false
    }
  }

  /**
   * Test storage service functionality
   */
  async testStorageService() {
    console.log('💾 Testing Storage Service...')
    
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      if (!user) {
        console.warn('⚠️ No authenticated user found')
        return false
      }

      console.log('👤 Testing with user:', user.id)
      
      // Test storage usage retrieval
      const usage = await storageService.getStorageUsage(user.id)
      console.log('📊 Storage usage:', usage)
      
      // Test quota check
      const canUpload = await storageService.canUpload(user.id, 1000000) // 1MB
      console.log('📤 Can upload 1MB:', canUpload)
      
      return true
    } catch (error) {
      console.error('❌ Storage service test failed:', error)
      return false
    }
  }

  /**
   * Test image library service
   */
  async testImageLibraryService() {
    console.log('🖼️ Testing Image Library Service...')
    
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      if (!user) {
        console.warn('⚠️ No authenticated user found')
        return false
      }

      // Test loading images
      const images = await imageLibraryService.getImages(user.id, {
        limit: 5,
        offset: 0
      })
      console.log('🖼️ Loaded images:', images.length)
      
      // Test getting stats
      const stats = await imageLibraryService.getUsageStats(user.id)
      console.log('📊 Usage stats:', stats)
      
      return true
    } catch (error) {
      console.error('❌ Image library service test failed:', error)
      return false
    }
  }

  /**
   * Test database connectivity
   */
  async testDatabase() {
    console.log('🗄️ Testing Database connectivity...')
    
    try {
      // Test if tables exist
      const { data: tables, error } = await supabase
        .from('information_schema.tables')
        .select('table_name')
        .in('table_name', ['user_image_library', 'user_storage_usage', 'ai_generated_images'])
      
      if (error) throw error
      
      console.log('📋 Available tables:', tables.map(t => t.table_name))
      
      if (tables.length < 3) {
        console.warn('⚠️ Some tables missing. Run migrations:')
        console.warn('  - 004_ai_generated_images_safe.sql')
        console.warn('  - 005_user_image_library_fixed.sql')
      }
      
      return true
    } catch (error) {
      console.error('❌ Database test failed:', error)
      return false
    }
  }

  /**
   * Run all tests
   */
  async runAll() {
    console.log('🚀 Running all Image Library tests...')
    console.log('=' .repeat(50))
    
    const results = {}
    
    results.modal = await this.testModalOpen()
    results.storage = await this.testStorageService()
    results.library = await this.testImageLibraryService()
    results.database = await this.testDatabase()
    
    console.log('=' .repeat(50))
    console.log('📊 Test Results:')
    Object.entries(results).forEach(([test, passed]) => {
      console.log(`  ${passed ? '✅' : '❌'} ${test}`)
    })
    
    const allPassed = Object.values(results).every(Boolean)
    console.log(`\n🎯 Overall: ${allPassed ? '✅ ALL TESTS PASSED' : '❌ SOME TESTS FAILED'}`)
    
    if (!allPassed) {
      console.log('\n📖 Setup guide: SETUP_IMAGE_LIBRARY.md')
    }
    
    return results
  }
}

// Create global instance
const imageLibraryTester = new ImageLibraryTester()

// Make available globally for browser console testing
if (typeof window !== 'undefined') {
  window.testImageLibrary = {
    testModalOpen: () => imageLibraryTester.testModalOpen(),
    testStorage: () => imageLibraryTester.testStorageService(),
    testLibrary: () => imageLibraryTester.testImageLibraryService(),
    testDatabase: () => imageLibraryTester.testDatabase(),
    runAll: () => imageLibraryTester.runAll()
  }
  
  console.log('🧪 Image Library tests available in browser console:')
  console.log('- window.testImageLibrary.testModalOpen()')
  console.log('- window.testImageLibrary.testStorage()')
  console.log('- window.testImageLibrary.testLibrary()')
  console.log('- window.testImageLibrary.testDatabase()')
  console.log('- window.testImageLibrary.runAll()')
}

export default imageLibraryTester
